import { useEffect, useState } from 'react';
import { Globe } from 'react-feather';
import type { DataItem } from '../../../../../utils/interface-types';

const MAX_STACK_ICONS = 5;

type SourceItem = NonNullable<DataItem['sources']>[number];

type WebSearchActivityProps = {
  isSearching: boolean;
  query?: string;
  sources?: DataItem['sources'];
  compact?: boolean;
};

function hostnameOf(source: { title?: string; link?: string; hostname?: string }): string {
  if (source.hostname) {
    return source.hostname;
  }
  if (source.link) {
    try {
      return new URL(source.link).hostname.replace(/^www\./i, '');
    } catch {
      return '';
    }
  }
  return source.title || '';
}

function truncateQuery(value: string, max = 48): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 1)}…`;
}

function uniqueSources(sources: DataItem['sources']): SourceItem[] {
  const seen = new Set<string>();
  const unique: SourceItem[] = [];
  (sources || []).forEach((item) => {
    const host = hostnameOf(item);
    if (!host || seen.has(host)) {
      return;
    }
    seen.add(host);
    unique.push({ ...item, hostname: host });
  });
  return unique;
}

function Favicon({ host, title, link }: { host: string; title?: string; link?: string }) {
  const icon = (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`}
      alt=""
      className="w-full h-full object-cover"
    />
  );
  const circleClass =
    'relative w-[22px] h-[22px] rounded-full border-2 border-white bg-zinc-100 overflow-hidden shrink-0';

  if (!link) {
    return (
      <span className={circleClass} title={title || host}>
        {icon}
      </span>
    );
  }
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`${circleClass} no-underline`}
      title={title || host}
    >
      {icon}
    </a>
  );
}

Favicon.defaultProps = {
  title: undefined,
  link: undefined,
};

function PlaceholderStack() {
  return (
    <div className="flex items-center" aria-hidden>
      {[0, 1, 2, 3].map((index) => (
        <span
          key={index}
          className="relative w-[22px] h-[22px] rounded-full border-2 border-white bg-zinc-200 shrink-0 animate-pulse"
          style={{ marginLeft: index === 0 ? 0 : -8, zIndex: 8 - index }}
        />
      ))}
    </div>
  );
}

function SiteStack({ sites }: { sites: SourceItem[] }) {
  const visible = sites.slice(0, MAX_STACK_ICONS);
  return (
    <div className="flex items-center">
      {visible.map((item, index) => (
        <span
          key={item.hostname || item.link || item.title}
          className="inline-flex"
          style={{ marginLeft: index === 0 ? 0 : -8, zIndex: visible.length - index }}
        >
          <Favicon host={item.hostname || ''} title={item.title} link={item.link} />
        </span>
      ))}
    </div>
  );
}

function WebSearchActivity({
  isSearching,
  query,
  sources,
  compact = false,
}: WebSearchActivityProps) {
  const sites = uniqueSources(sources);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isSearching || sites.length < 2) {
      setActiveIndex(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % sites.length);
    }, 1400);
    return () => window.clearInterval(timer);
  }, [isSearching, sites.length]);

  if (!isSearching && sites.length === 0) {
    return (
      <div className={compact ? 'mt-3' : 'mb-3'} role="status">
        <div className="inline-flex items-center gap-2 text-xs text-zinc-600">
          <Globe size={14} className="shrink-0" />
          <span>Searched the web</span>
        </div>
      </div>
    );
  }

  const featured = sites[Math.min(activeIndex, Math.max(sites.length - 1, 0))];
  const featuredHost = featured?.hostname || '';
  let statusLabel = 'Searched the web';
  if (isSearching && sites.length > 0 && featuredHost) {
    if (sites.length === 1) {
      statusLabel = `Searching ${featuredHost}…`;
    } else {
      statusLabel = `Searching ${featuredHost} and ${sites.length - 1} more…`;
    }
  } else if (isSearching) {
    const rawQuery = (query || '').trim();
    if (rawQuery) {
      statusLabel = `Searching the web for “${truncateQuery(rawQuery)}”…`;
    } else {
      statusLabel = 'Searching the web…';
    }
  } else if (sites.length > 0) {
    statusLabel = `Searched ${sites.length} site${sites.length === 1 ? '' : 's'}`;
  }

  return (
    <div
      className={compact ? 'mt-3' : 'mb-3'}
      role="status"
      aria-live="polite"
      aria-label={statusLabel}
    >
      <div
        className={`inline-flex items-center gap-2 text-xs ${
          isSearching ? 'text-[#0A8BC7]' : 'text-zinc-600'
        }`}
      >
        {sites.length > 0 ? <SiteStack sites={sites} /> : <PlaceholderStack />}
        <span>{statusLabel}</span>
      </div>
    </div>
  );
}

WebSearchActivity.defaultProps = {
  query: undefined,
  sources: undefined,
  compact: false,
};

export default WebSearchActivity;
